import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  clearStorageBrowseWizardState,
  revokeStorageBrowseSession,
  startStoragePicker,
} from "./storage-registration";
import { getActiveOrganizationScopeId } from "@/lib/organization-scope";

/** OAuth completion is distinct from Storage materialization. The PF picker owns
 * the folder hierarchy and returns a one-time selection handle to the BFF. */
export function StorageBrowseResultPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const organizationScopeId = getActiveOrganizationScopeId();
  const browseSessionId = params.get("browseSessionId") ?? "";
  const outcome = params.get("outcome") ?? "state_invalid";
  const started = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!browseSessionId || outcome !== "success" || started.current) return;
    started.current = true;
    void startStoragePicker(browseSessionId).then(({ pickerUrl }) => {
      window.location.assign(pickerUrl);
    }).catch((failure: unknown) => {
      setError(failure instanceof Error ? failure.message : "フォルダ選択画面を開始できませんでした。");
    });
  }, [browseSessionId, outcome]);

  async function cancel() {
    if (!browseSessionId) {
      navigate("/console/storages/new");
      return;
    }
    setCancelling(true);
    try {
      await revokeStorageBrowseSession(browseSessionId);
      clearStorageBrowseWizardState(organizationScopeId);
      navigate("/console/storages/new");
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "認可の取り消しに失敗しました。");
    } finally {
      setCancelling(false);
    }
  }

  return <main className="mx-auto max-w-2xl space-y-4 p-6">
    <h1 className="text-2xl font-bold">クラウド認可</h1>
    {outcome === "success" && !error
      ? <p role="status">認可が完了しました。フォルダ選択画面へ移動しています。ストレージ登録はまだ完了していません。</p>
      : <p role="alert">{error ?? "認可を完了できませんでした。登録をやり直してください。"}</p>}
    <div className="flex gap-2">
      {outcome === "success" && error && <Button onClick={() => {
        started.current = false;
        setError(null);
        void startStoragePicker(browseSessionId).then(({ pickerUrl }) => window.location.assign(pickerUrl))
          .catch((failure: unknown) => setError(failure instanceof Error ? failure.message : "開始できませんでした。"));
      }}>再試行</Button>}
      <Button variant="outline" disabled={cancelling} onClick={() => void cancel()}>登録を取り消す</Button>
    </div>
  </main>;
}
